import { useState } from 'react'
import { Dropdown, Menu, Spin } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons/faEllipsisV'
import { useTranslation } from 'react-i18next'
import type { SurveyTemplateRow } from '../../common/types'
import { TRANSLATION_PREFIX } from '../../constants'
import { OptionsTrigger } from './styled'

interface IProps {
  row: SurveyTemplateRow
  busy: boolean
  onEdit: (row: SurveyTemplateRow) => void
  onDuplicate: (row: SurveyTemplateRow) => void
  onArchive: (row: SurveyTemplateRow) => void
  onRestore: (row: SurveyTemplateRow) => void
}

const popupContainer = () => document.getElementById('lmsPage') as HTMLElement

/**
 * Row actions of the survey bank. Active surveys can be edited, duplicated or
 * archived; archived ones can only be restored. Surveys are never deleted.
 */
export const SurveyTemplateOptions = ({
  row,
  busy,
  onEdit,
  onDuplicate,
  onArchive,
  onRestore,
}: IProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const label = (key: string) => t(`${TRANSLATION_PREFIX}.actions.${key}`)

  const menu = row.archived ? (
    <Menu>
      <Menu.Item
        key="restore"
        data-testid="survey-bank-action-restore"
        onClick={() => onRestore(row)}
      >
        {label('restore')}
      </Menu.Item>
    </Menu>
  ) : (
    <Menu>
      <Menu.Item
        key="edit"
        data-testid="survey-bank-action-edit"
        onClick={() => onEdit(row)}
      >
        {label('edit')}
      </Menu.Item>
      <Menu.Item
        key="duplicate"
        data-testid="survey-bank-action-duplicate"
        onClick={() => onDuplicate(row)}
      >
        {label('duplicate')}
      </Menu.Item>
      <Menu.Item
        key="archive"
        data-testid="survey-bank-action-archive"
        onClick={() => onArchive(row)}
      >
        {label('archive')}
      </Menu.Item>
    </Menu>
  )

  return (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      placement="bottomRight"
      visible={open}
      onVisibleChange={setOpen}
      getPopupContainer={popupContainer}
      disabled={busy}
    >
      <OptionsTrigger
        type="button"
        data-testid={`survey-bank-options-${row.id}`}
        aria-label={label('open')}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
      >
        {busy ? <Spin size="small" /> : <FontAwesomeIcon icon={faEllipsisV} />}
      </OptionsTrigger>
    </Dropdown>
  )
}
